import { type FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdArchive, MdUnarchive, MdDelete, MdArrowBack } from 'react-icons/md';
import { toast } from 'react-toastify';
import type { Task } from '../types';
import { getTasks, unarchiveTask, deleteTask } from '../services/api';
import { useTheme } from '../hooks/useTheme';
import { ArchiveSkeleton } from '../components/Skeleton';
import TaskCard from '../components/TaskCard';

const Archive: FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArchivedTasks();
  }, []);

  const loadArchivedTasks = async () => {
    try {
      setLoading(true);
      const data = await getTasks({ is_archived: true });
      setTasks(data);
    } catch (error) {
      console.error('Failed to load archived tasks:', error);
      toast.error('Failed to load archived tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (taskId: string) => {
    try {
      await unarchiveTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success('Task unarchived');
    } catch (error) {
      console.error('Failed to unarchive task:', error);
      toast.error('Failed to unarchive task');
    }
  };

  const handlePermanentDelete = async (taskId: string) => {
    if (!confirm('Permanently delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  if (loading) {
    return <ArchiveSkeleton />;
  }

  // Get accent color with opacity for gradient
  const getAccentColor = () => {
    const colors: Record<string, string> = {
      blue: '0, 255, 255',
      green: '0, 255, 0',
      purple: '139, 0, 255',
      pink: '255, 0, 255',
      orange: '255, 107, 0',
      yellow: '255, 255, 0'
    };
    return colors[document.documentElement.style.getPropertyValue('--color-theme-name')?.trim() || 'blue'];
  };

  return (
    <div 
      className="min-h-screen py-8 px-4"
      style={{
        background: theme === 'dark'
          ? `linear-gradient(135deg, rgba(17, 24, 39, 1) 0%, rgba(${getAccentColor()}, 0.03) 100%)`
          : `linear-gradient(135deg, rgba(240, 244, 248, 1) 0%, rgba(${getAccentColor()}, 0.05) 100%)`
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <MdArchive className="w-8 h-8 dark:text-neon-purple text-purple-600" />
              <h1 className="text-3xl font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                Archived Tasks
              </h1>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="
                flex items-center gap-2
                px-4 py-2
                rounded-lg
                dark:bg-dark-card
                bg-white
                border-2
                dark:border-theme
                border-theme
                dark:text-theme
                text-theme
                font-semibold
                hover:shadow-theme
                focus:outline-none
                focus:ring-2
                focus:ring-theme
                transition-all
              "
            >
              <MdArrowBack className="w-5 h-5" />
              Back to Dashboard
            </motion.button>
          </div>
          <p style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
            View and restore your archived tasks
          </p>
        </motion.div>

        {/* Empty State */}
        {tasks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="
              text-center
              py-16
              dark:bg-dark-surface
              bg-light-surface
              rounded-lg
              border-2 border-dashed
              dark:border-dark-hover
              border-gray-300
            "
          >
            <MdArchive className="
              w-20 h-20
              mx-auto mb-4
              dark:text-gray-600
              text-gray-400
            " />
            <h2 className="text-xl font-semibold mb-2" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
              No Archived Tasks
            </h2>
            <p style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
              Tasks you archive will appear here
            </p>
          </motion.div>
        ) : (
          <>
            {/* Task Count */}
            <div className="mb-4 text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
              {tasks.length} archived task{tasks.length !== 1 ? 's' : ''}
            </div>

            {/* Task Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map((task) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="relative"
                >
                  {/* Archived badge overlay */}
                  <div className="
                    absolute top-2 right-2 z-10
                    flex items-center gap-1
                    px-2 py-1
                    rounded
                    dark:bg-neon-purple/30
                    bg-purple-200
                    dark:text-neon-purple
                    text-purple-700
                    text-xs font-semibold
                    backdrop-blur-sm
                  ">
                    <MdArchive className="w-3 h-3" />
                    Archived
                  </div>

                  {/* Task Card with custom actions */}
                  <div className="relative">
                    <TaskCard
                      task={task}
                      onEdit={() => {}} // Disabled in archive
                      onDelete={handlePermanentDelete}
                      onArchive={() => {}} // Not needed here
                    />
                    
                    {/* Unarchive button overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleUnarchive(task.id)}
                        className="
                          w-full
                          flex items-center justify-center gap-2
                          px-4 py-2
                          rounded-lg
                          bg-gradient-to-r from-neon-green to-green-500
                          text-white
                          font-semibold
                          shadow-md
                          hover:shadow-neon
                          focus:outline-none
                          focus:ring-2
                          focus:ring-neon-green
                          transition-all
                        "
                      >
                        <MdUnarchive className="w-5 h-5" />
                        Unarchive
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Bulk Actions Info */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="
                mt-8
                p-4
                rounded-lg
                dark:bg-dark-surface
                bg-light-surface
                border-2
                dark:border-neon-yellow/50
                border-yellow-400
              "
            >
              <div className="flex items-start gap-3">
                <div className="
                  p-2
                  rounded
                  dark:bg-neon-yellow/20
                  bg-yellow-100
                ">
                  <MdDelete className="w-5 h-5 dark:text-neon-yellow text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                    Permanent Deletion
                  </h3>
                  <p className="text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                    Use the delete button on each task to permanently remove it. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default Archive;
